/**
 * @criptohomer/disguise
 *
 * Transforma o quadro binario ja cifrado em algo que o WhatsApp ou o
 * Telegram transportem sem chamar atencao imediata.
 *
 * O QUE ISTO NAO FAZ, e esta na UI do app, nao so aqui: disfarce esconde o
 * CONTEUDO, nunca o FATO da comunicacao. O operador do transporte continua
 * vendo quem fala com quem, quando e com que volume. E uma sequencia de 300
 * emojis e estatisticamente obvia para quem procura por ela — a meta e nao
 * chamar atencao casual, nao resistir a analise dirigida.
 */
import { decodeCompact, encodeCompact, looksLikeCompact } from './compact.js'
import { decodeEmoji, encodeEmoji, looksLikeEmoji } from './emoji.js'
import { decodeMedia, encodeMedia } from './media.js'
import { isPng } from './png.js'
import { decodeWords, encodeWords, looksLikeWords } from './words.js'
import { DisguiseError, DisguiseKind, MediaCarrier, type Disguised } from './types.js'
import { isWav } from './wav.js'

export const DISGUISE_VERSION = '0.1.0' as const

export interface EncodeOptions {
  /** Portador do codec `media`. Padrao: PNG. */
  readonly carrier?: MediaCarrier
}

export function encode(
  payload: Uint8Array,
  kind: DisguiseKind,
  options: EncodeOptions = {},
): Disguised {
  switch (kind) {
    case DisguiseKind.Emoji:
      return { kind, transport: 'text', text: encodeEmoji(payload) }
    case DisguiseKind.UnicodeWords:
      return { kind, transport: 'text', text: encodeWords(payload) }
    case DisguiseKind.Compact:
      return { kind, transport: 'text', text: encodeCompact(payload) }
    case DisguiseKind.Media:
      return encodeMedia(payload, options.carrier ?? MediaCarrier.Png)
    default:
      throw new DisguiseError(`codec desconhecido: ${String(kind)}`)
  }
}

/**
 * Detecta o codec e decodifica. Aceita texto colado pelo usuario (iOS) ou o
 * arquivo vindo do share intent (Android).
 */
export function decode(input: string | Uint8Array): Uint8Array {
  if (typeof input !== 'string') return decodeMedia(input)

  const trimmed = input.trim()
  if (trimmed.length === 0) throw new DisguiseError('entrada vazia')
  if (looksLikeEmoji(trimmed)) return decodeEmoji(trimmed)
  if (looksLikeCompact(trimmed)) return decodeCompact(trimmed)
  if (looksLikeWords(trimmed)) return decodeWords(trimmed)
  throw new DisguiseError('texto nao corresponde a nenhum codec conhecido')
}

/** Qual codec produziu esta entrada, sem tentar decodificar. */
export function detect(input: string | Uint8Array): DisguiseKind | undefined {
  if (typeof input !== 'string') {
    return isPng(input) || isWav(input) ? DisguiseKind.Media : undefined
  }
  const trimmed = input.trim()
  if (looksLikeEmoji(trimmed)) return DisguiseKind.Emoji
  if (looksLikeCompact(trimmed)) return DisguiseKind.Compact
  if (looksLikeWords(trimmed)) return DisguiseKind.UnicodeWords
  return undefined
}

export { EMOJI_ALPHABET } from './emoji-alphabet.js'
export { WORD_ALPHABET } from './word-alphabet.js'
export { EMOJI_BASE, EMOJI_BITS, decodeEmoji, encodeEmoji, looksLikeEmoji } from './emoji.js'
export {
  COMPACT_BASE,
  COMPACT_BITS,
  COMPACT_FIRST_CODE_POINT,
  COMPACT_LAST_CODE_POINT,
  decodeCompact,
  encodeCompact,
  looksLikeCompact,
} from './compact.js'
export { WORD_BASE, WORD_BITS, decodeWords, encodeWords, looksLikeWords } from './words.js'
export { decodePng, encodePng, isPng } from './png.js'
export { decodeWav, encodeWav, isWav } from './wav.js'
export { decodeMedia, encodeMedia } from './media.js'
export {
  DisguiseError,
  DisguiseKind,
  MediaCarrier,
  R2_OFFLOAD_THRESHOLD_BYTES,
  REQUIRED_TRANSPORT,
  assertTransport,
} from './types.js'
export type { Disguised, DisguisedFile, DisguisedText, Transport } from './types.js'
export {
  LENGTH_PREFIX_BYTES,
  fromBits,
  fromSymbols,
  stripLengthPrefix,
  toBits,
  toSymbols,
  withLengthPrefix,
} from './bitstream.js'
